import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Panel } from "@/components/portal/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Lock, ShieldCheck } from "lucide-react";

export default function Setup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/bootstrap-status");
        setStatus(data);
        if (data.allowed_email) setEmail(data.allowed_email);
      } catch {
        setStatus({ can_bootstrap: false });
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/bootstrap", { email: email.trim(), password, token: token || undefined });
      setUser(data);
      toast.success("Admin account created");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background grain p-6">
      <div className="w-full max-w-md">
        <div className="mb-8"><Logo size={40} /></div>
        <Panel className="p-8">
          {status === null ? (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Checking…</div>
          ) : status.can_bootstrap ? (
            <>
              <ShieldCheck className="h-7 w-7 text-[#E1FF00] mb-4" />
              <h2 className="font-display text-2xl text-white mb-1">Create the owner account</h2>
              <p className="text-sm text-muted-foreground mb-6">One-time setup for the authorized admin email. Choose a strong password.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="setup-form">
                <div className="space-y-2">
                  <Label>Admin email</Label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required data-testid="setup-email-input" className="bg-secondary border-border h-11" />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} type="password" minLength={10} required placeholder="At least 10 characters" data-testid="setup-password-input" className="bg-secondary border-border h-11" />
                </div>
                {status.requires_token && (
                  <div className="space-y-2">
                    <Label>Bootstrap token</Label>
                    <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="Provided through a trusted channel" data-testid="setup-token-input" className="bg-secondary border-border h-11" />
                  </div>
                )}
                {error && <div className="text-sm text-[#FF6666]" data-testid="setup-error">{error}</div>}
                <Button type="submit" disabled={loading} className="w-full h-11 bg-white text-black hover:bg-white/90 font-semibold" data-testid="setup-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create admin & sign in"}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="setup-locked">
              <Lock className="h-7 w-7 text-muted-foreground mb-4" />
              <h2 className="font-display text-2xl text-white mb-2">Setup is locked</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {status.admin_exists
                  ? "An administrator is already configured. Sign in from the login page, or ask an admin to invite you."
                  : "Admin bootstrap is disabled. It stays locked until it is enabled for the authorized admin email through a trusted channel."}
              </p>
              <Button variant="outline" className="mt-6 border-border" onClick={() => navigate("/login")} data-testid="setup-goto-login">
                Go to sign in
              </Button>
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
